import styles from './AddComment.module.css';
import { Slate, Editable, withReact } from 'slate-react';

import React, { useState, useCallback, useEffect, useContext } from 'react';
import { withLinks, LinkButton, RemoveLinkButton } from './editor/LinkButton';
import { createEditor, Descendant, Element as SlateElement } from 'slate';

import { Toolbar } from './editor/Toolbar';
import { MarkButton } from './editor/MarkButton';
import { Element, Leaf } from './editor/Renderer';
import Avatar from 'components/common/Avatar';
import { UserContext } from 'contexts/user';
import {
  addCommunityComment,
  replyToCommunityComment,
} from 'lib/api/community_api';
import { CommunityShotItem } from 'types/community';
import VerifyEmail from './VerifyEmail';
import LoginRedirect from './LoginRedirect';
import * as analytics from 'lib/analytics';

type Props = {
  shotItem: CommunityShotItem;
  replyToCommentId?: number;
  submitCallback?: Function;
};

const clearEditorValue: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

function isEmpty(value: Descendant[]): Boolean {
  // omg i guess we can have just new lines...
  // should probably make this if we find any text anywhere we know its not empty and can bail.
  if (
    value.length === 1 &&
    SlateElement.isElement(value[0]) &&
    value[0].children !== undefined &&
    value[0].children.length === 1 &&
    value[0].children[0].text.trim() === ''
  ) {
    return true;
  }
  return false;
}

function AddComment({ shotItem, replyToCommentId, submitCallback }: Props) {
  const { user } = useContext(UserContext);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const [editor] = useState(() => withLinks(withReact(createEditor())));
  const [value, setValue] = useState<Descendant[]>(clearEditorValue);
  const [canPost, setCanPost] = useState<boolean>(false);

  function _isLoggedIn(): boolean {
    return user !== undefined && user.signedIn;
  }

  function _isVerified(): boolean {
    return user !== undefined && user.verified === true;
  }

  function _autofocus(): boolean {
    if (replyToCommentId) {
      return true;
    }
    return false;
  }

  const submitComment = async () => {
    // disable posting (so we cant spam post), making a change will re enable it...
    setCanPost(false);
    editor.marks = null;
    editor.selection = null;
    if (replyToCommentId) {
      await replyToCommunityComment({
        communityPostId: shotItem.communityPostId,
        communityRevisionId: shotItem.communityRevisionId,
        commentData: { comment: value },
        replyToCommentId: replyToCommentId,
      });
      analytics.commentsSendReply(
        replyToCommentId,
        shotItem.communityPostId,
        shotItem.title,
        shotItem.fileId
      );
    } else {
      await addCommunityComment({
        communityPostId: shotItem.communityPostId,
        communityRevisionId: shotItem.communityRevisionId,
        commentData: { comment: value },
      });
      analytics.commentsSendComment(
        shotItem.communityPostId,
        shotItem.title,
        shotItem.fileId
      );
    }
    if (submitCallback) {
      submitCallback();
    }

    setValue(clearEditorValue);
  };

  const onTextChange = (newValue: Descendant[]) => {
    setValue(newValue);
    if (isEmpty(newValue)) {
      setCanPost(false);
    } else if (canPost !== true) {
      setCanPost(true);
    }
  };

  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  function getClass() {
    if (replyToCommentId) return styles.AddReply;
    return styles.AddComment;
  }

  if (!mounted) {
    return <div />;
  }

  if (!_isLoggedIn()) {
    return (
      <div className={getClass()}>
        <LoginRedirect />
      </div>
    );
  }

  if (!_isVerified()) {
    return (
      <div className={getClass()}>
        <VerifyEmail />
      </div>
    );
  }

  return (
    <div className={getClass()}>
      <Slate editor={editor} value={value} onChange={onTextChange}>
        <div className={styles.SlateComment}>
          <div
            style={{
              padding: 6,
              width: 40,
              height: 40,
              flexGrow: 0,
              flexShrink: 0,
            }}
          >
            {user !== undefined && <Avatar data={user} />}
          </div>

          <Editable
            autoFocus={_autofocus()}
            autoCapitalize="false"
            autoCorrect="false"
            spellCheck="false"
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder="Add a comment..."
            className={styles.SlateEditable}
            onKeyDown={(event) => {
              // Note, posting on keydown because the meta-key & Enter keyup event does not fire (ctrl does)
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                canPost && submitComment();
                event.preventDefault();
              }
            }}
          />
        </div>
        <Toolbar className={styles.SlateToolbar}>
          <MarkButton format="bold" icon="format_bold" />
          <MarkButton format="italic" icon="format_italic" />
          <MarkButton format="strikethrough" icon="format_strikethrough" />
          <MarkButton format="code" icon="code" />
          <LinkButton />
          <RemoveLinkButton />
          <button
            className={
              canPost
                ? styles.CommunityPostButton
                : styles.CommunityPostButtonDisabled
            }
            style={{
              marginLeft: 'auto',
            }}
            onClick={() => {
              canPost && submitComment();
            }}
          >
            {replyToCommentId === undefined && 'Comment'}
            {replyToCommentId !== undefined && 'Reply'}
          </button>
        </Toolbar>
      </Slate>
    </div>
  );
}

// TODO: could probably just pass avatar in ...
export default AddComment;
